import { useCallback, useState } from 'react';

import { SessionFormSection } from './components/SessionFormSection';
import {
  IaPromptName,
  IaScreenerQuestion,
  Session,
  StudyType,
  useIsAdmin,
  useMarkStudyStepAsValidated,
  usePlan,
} from '@app/api';
import {
  ScreeningBuilderForm,
  useRequestBillingInformation,
  useToast,
} from 'components';
import { getMaxNumberOfQuestions } from 'lib/billing';
import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import { useIaSuggestion } from './hooks/useIaSuggestion';
import { ApiFormattedException } from 'store/modules/exceptions';
import { useTranslation } from 'react-i18next';
import { GDPRConsentRef } from 'lib/criteriaUtils';
import { ScreeningGdprSection } from './components/ScreeningGdprSection';
import { ScreeningIaSuggestion } from './components/ScreeningIaSuggestion';
import { IaSuggestionButton } from './components/IaSuggestionButton';
import { FieldWithCriterion } from 'lib/iaScreenerUtils';

interface Props {
  session: Session;
}

export const EditSessionScreening = ({ session }: Props) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const { data: plan } = usePlan(session.planId);
  const maxQuestions = getMaxNumberOfQuestions(plan?.products);
  const requestBillingInformation = useRequestBillingInformation();
  const toast = useToast();

  const {
    available: iaSuggestionAvailable,
    attempts: iaSuggestionAttempts,
    ready: iaSuggestionReady,
    request: requestIaSuggestion,
    requestFirst: requestFirstIaSuggestion,
    generating: iaSuggestionGenerating,
  } = useIaSuggestion<{ questions: IaScreenerQuestion[] }>(
    session,
    IaPromptName.SCREENER,
  );
  const hasIaQuestions = iaSuggestionAttempts.length > 0;
  const [iaSuggestionOpen, setIaSuggestionOpen] = useState(false);

  const onSubmit = useCallback(() => {
    setIsSaving(true);
  }, [setIsSaving]);

  const onFinish = useCallback(() => {
    setIsSaving(false);
  }, [setIsSaving]);

  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );
  const redirectIfDraft = useRedirectIfDraft(session);

  const onSuccess = useCallback(async () => {
    await markStepAsValidated({ step: 'screening' });
    redirectIfDraft();
  }, [redirectIfDraft, markStepAsValidated]);

  const onError = useCallback(
    async (error: Error | ApiFormattedException) => {
      setIsSaving(false);
      if ('getCode' in error && error.getCode() === 'INCOMPLETE_BILLING_INFO') {
        await requestBillingInformation();
      }
    },
    [requestBillingInformation],
  );

  const isAdmin = useIsAdmin();

  return (
    <ScreeningBuilderForm
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      onFinish={onFinish}
      onError={onError}
      recruitmentId={session.recruitmentId}
      sessionId={session.id}
      organizationId={session.organizationId}
      maxNumberOfQuestions={maxQuestions}
      numberOfParticipantRequired={session.numberOfParticipantRequired}
    >
      {({
        builderNode,
        profileValidationNode,
        addFieldWithCriterion,
        addFieldsWithCriteria,
        currentForm,
      }) => {
        const hasQuestion = !!currentForm?.fields?.some(
          (field) => field.ref !== GDPRConsentRef,
        );
        const acceptSuggestion = (items: FieldWithCriterion[]) => {
          addFieldsWithCriteria(items);
          setIaSuggestionOpen(false);
          toast.success(t('editSession.screening.iaSuggestion.added'));
        };

        return (
          <EditSessionLayout
            session={session}
            footer={<SessionSubmitLine session={session} saving={isSaving} />}
          >
            {(session.studyType === StudyType.MODERATED || isAdmin) && (
              <SessionFormSection
                title={t('editSession.screening.profileValidation.title')}
              >
                {profileValidationNode}
              </SessionFormSection>
            )}
            <SessionFormSection
              title={t('editSession.screening.title')}
              description={t('editSession.screening.sideNote')}
            >
              <ScreeningGdprSection
                questionExist={
                  !!currentForm?.fields?.some(
                    (field) => field.ref === GDPRConsentRef,
                  )
                }
                addFieldToForm={addFieldWithCriterion}
              />
              {!hasQuestion && iaSuggestionAvailable && (
                <>
                  <IaSuggestionButton
                    text={t('editSession.screening.iaSuggestion.generate')}
                    isLoading={
                      !iaSuggestionReady ||
                      (iaSuggestionGenerating && !hasIaQuestions)
                    }
                    onClick={() => {
                      requestFirstIaSuggestion();
                      setIaSuggestionOpen(true);
                    }}
                  />
                  <ScreeningIaSuggestion
                    attempts={iaSuggestionAttempts}
                    generating={iaSuggestionGenerating}
                    isOpen={iaSuggestionOpen}
                    onClose={() => setIaSuggestionOpen(false)}
                    onRetry={requestIaSuggestion}
                    onAccept={acceptSuggestion}
                  />
                </>
              )}
              {builderNode}
            </SessionFormSection>
          </EditSessionLayout>
        );
      }}
    </ScreeningBuilderForm>
  );
};
